import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { EditIcon, CloseIcon, UserIcon, SearchIcon } from '../components/Icons';

export default function AdminUsers() {
  const { faculty } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [search, setSearch] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    department: '',
    designation: '',
    employeeId: ''
  });

  const loadUsers = async () => {
    try {
      const res = await api.get('/auth/admin/users');
      setUsers(res.data.users || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'faculty',
      department: user.department || '',
      designation: user.designation || '',
      employeeId: user.employeeId || ''
    });
    setEditModalOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setUpdatingId(selectedUser._id);
    try {
      const res = await api.put(`/auth/admin/user/${selectedUser._id}`, editFormData);
      if (res.data.success) {
        toast.success('User updated successfully');
        setUsers(users.map(u => u._id === selectedUser._id ? res.data.faculty : u));
        setEditModalOpen(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner" />
      </div>
    );
  }

  const filteredUsers = search.trim()
    ? users.filter(
        (u) =>
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">User Management</div>
          <div className="page-subtitle">Manage faculty and administrator roles</div>
        </div>
      </div>

      <div className="page-body">
        {/* Search */}
        <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <SearchIcon style={{ position: 'absolute', left: '1rem', color: 'var(--text-3)', width: 18, height: 18 }} />
            <input
              style={{ paddingLeft: '3rem', height: '3rem', background: 'var(--surface-1)' }}
              className="toolbar-input"
              placeholder="Search faculty by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="card">
          {filteredUsers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">USERS</div>
              <div className="empty-title">No users found</div>
              <div className="empty-text">{search ? 'No users match your search.' : 'No faculty members have registered yet.'}</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '4rem' }}>S.No</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th style={{ width: '3rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u, index) => (
                    <tr key={u._id}>
                      <td style={{ color: 'var(--text-3)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                        {index + 1}
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <div className="cell-stack-title" style={{ fontSize: '14px' }}>{u.name}</div>
                      </td>
                      <td style={{ verticalAlign: 'middle', color: 'var(--text-2)', fontSize: '13px' }}>{u.email}</td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <span style={{ 
                          padding: '0.375rem 0.75rem', 
                          borderRadius: '8px', 
                          fontSize: '11px', 
                          fontWeight: 700, 
                          textTransform: 'uppercase',
                          letterSpacing: '0.02em',
                          background: u.role === 'super_admin' ? 'rgba(220, 38, 38, 0.08)' : (u.role === 'iqac' ? 'rgba(0, 95, 167, 0.08)' : 'var(--surface-1)'),
                          color: u.role === 'super_admin' ? '#dc2626' : (u.role === 'iqac' ? '#005fa7' : 'var(--text-2)'),
                          border: `1px solid ${u.role === 'super_admin' ? 'rgba(220, 38, 38, 0.15)' : (u.role === 'iqac' ? 'rgba(0, 95, 167, 0.15)' : 'var(--border-1)')}`,
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}>
                          {u.role.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={{ verticalAlign: 'middle', textAlign: 'right' }}>
                        <button 
                          className="icon-btn" 
                          onClick={() => handleEditClick(u)}
                          title="Edit User"
                          style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--surface-1)' }}
                        >
                          <EditIcon style={{ width: 18, height: 18, color: 'var(--navy)' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      {editModalOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '480px', borderRadius: '24px' }}>
            <div className="modal-header" style={{ padding: '2rem 2rem 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'var(--blue-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--navy)' }}>
                  <UserIcon style={{ width: 24, height: 24 }} />
                </div>
                <div>
                  <h2 className="modal-title" style={{ fontSize: '1.25rem' }}>Edit User</h2>
                  <p style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>Update administrative access and details</p>
                </div>
              </div>
              <button className="modal-close" onClick={() => setEditModalOpen(false)} style={{ borderRadius: '12px' }}>
                <CloseIcon />
              </button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="modal-body" style={{ padding: '2rem' }}>
                <div className="field">
                  <label style={{ fontSize: '10px', color: 'var(--navy)', opacity: 0.8 }}>Full Name</label>
                  <input 
                    type="text" 
                    required 
                    style={{ height: '3.5rem', borderRadius: '14px', fontSize: '15px', fontWeight: 600 }}
                    value={editFormData.name} 
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} 
                  />
                </div>

                <div className="field">
                  <label style={{ fontSize: '10px', color: 'var(--navy)', opacity: 0.8 }}>Email Address</label>
                  <input 
                    type="email" 
                    required 
                    style={{ height: '3.5rem', borderRadius: '14px', fontSize: '15px', fontWeight: 600 }}
                    value={editFormData.email} 
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} 
                  />
                </div>

                <div className="grid-2" style={{ gap: '1.25rem' }}>
                  <div className="field">
                    <label style={{ fontSize: '10px', color: 'var(--navy)', opacity: 0.8 }}>Phone Number</label>
                    <input 
                      type="tel" 
                      style={{ height: '3.5rem', borderRadius: '14px', fontSize: '15px', fontWeight: 600 }}
                      value={editFormData.phone} 
                      onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })} 
                    />
                  </div>
                  <div className="field">
                    <label style={{ fontSize: '10px', color: 'var(--navy)', opacity: 0.8 }}>System Role</label>
                    <select
                      style={{ height: '3.5rem', borderRadius: '14px', fontSize: '14px', fontWeight: 700 }}
                      value={editFormData.role}
                      onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                      disabled={selectedUser?._id === faculty.id}
                    >
                      <option value="faculty">Faculty</option>
                      <option value="ao">Administrative Officer (AO)</option>
                      <option value="security">Gate Security</option>
                      <option value="iqac">IQAC Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </div>
                </div>
                
                {selectedUser?._id === faculty.id && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: 'var(--surface-1)', borderRadius: '10px', border: '1px dashed var(--border-1)', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ color: 'var(--text-3)', fontSize: '11px', lineHeight: 1.4 }}>
                      You are editing your own profile. Role selection is disabled to maintain your admin privileges.
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ padding: '1.5rem 2rem 2rem', background: 'transparent', borderTop: 'none' }}>
                <button type="button" className="btn btn-ghost" style={{ borderRadius: '14px', height: '3.5rem', padding: '0 1.5rem' }} onClick={() => setEditModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-navy" style={{ borderRadius: '14px', height: '3.5rem', padding: '0 2rem', flex: 1 }} disabled={updatingId === selectedUser?._id}>
                  {updatingId === selectedUser?._id ? 'Saving...' : 'Save User Details'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
